import React, { useEffect, useMemo, useRef, useState } from 'react';
import appNoticeService from '../../services/appNoticeService';
import { portalService } from '../../services';
import { geocodeCities } from '../CityPicker';
import { RADIUS_OPTIONS } from '../admin/shell/useArea';
import { StudioCard, PILL, pillOf, SearchIcon, Icon, ICON } from '../broadcast/studioUi';

/**
 * WHO SEES IT — the audience an admin message reaches. ONE editor, shared by the sheet
 * composer and the push composer so both speak the same scopes as signals
 * (design_handoff_app_notices "WHO SEES IT"):
 *
 *   EVERYONE · HOSTS · GUESTS · PROS · CLUB MEMBERS · SPECIFIC PEOPLE · NEW SIGNUPS
 *
 * Scopes OR together. CITIES is a real geofence (centre + radius) ANDed on top: pick one
 * city or several, and only people inside one of them see it. REACH asks the server how
 * many people that is right now (debounced 400 ms) and hands the count up through
 * `onReach` so the finish button can say PUBLISH TO 2,744 PEOPLE.
 *
 * value = { audience: { scopes, clubIds, userIds, newWithinDays, labels }, cities: [{ slug, label, lat, lng, radiusMiles }] }
 */
export const SCOPES = [
  { key: 'everyone', label: 'EVERYONE', hint: 'Every real account.' },
  { key: 'hosts', label: 'HOSTS', hint: 'Hosted at least one event.' },
  { key: 'guests', label: 'GUESTS', hint: 'Accepted or paid on at least one event.' },
  { key: 'pros', label: 'PROS', hint: 'Owners of a claimed Pro page.' },
  { key: 'clubs', label: 'CLUB MEMBERS', hint: 'Members of the clubs you pick.' },
  { key: 'select', label: 'SPECIFIC PEOPLE', hint: 'Only the people you name.' },
  { key: 'new', label: 'NEW SIGNUPS', hint: 'Signed up in the last N days.' },
];

export const BLANK_AUDIENCE = { scopes: [], clubIds: [], userIds: [], newWithinDays: null, labels: {} };

const cityName = (label) => (label || '').split(',')[0].trim();
const initials = (name) => (name || '?').split(/\s+/).map((x) => x[0]).join('').slice(0, 2).toUpperCase();
const peopleNoun = (n) => `${Number(n || 0).toLocaleString()} ${n === 1 ? 'PERSON' : 'PEOPLE'}`;

/** Row summary, matching the server's AudienceSummary + city names. */
export function targetLabel(row) {
  const who = row.audienceSummary || 'EVERYONE';
  const cities = (row.cities || []).map((c) => cityName(c.label).toUpperCase());
  if (cities.length === 0 && row.targetCity) cities.push(row.targetCity.toUpperCase());
  return cities.length ? `${who} · ${cities.join(', ')}` : who;
}

/** The same sentence for a draft — the strip under the editor. */
export function describeTarget(audience, cities) {
  const scopes = audience.scopes || [];
  const who = scopes.length === 0 ? 'EVERYONE' : scopes.map((k) => (SCOPES.find((s) => s.key === k) || { label: k.toUpperCase() }).label).join(' + ');
  return cities.length ? `${who} · ${cities.map((c) => cityName(c.label).toUpperCase()).join(', ')}` : who;
}

const Label = ({ children }) => <span className="font-mono text-[10px] font-bold tracking-[0.16em] text-[#6B7280]">{children}</span>;

function PillSearch({ value, onChange, placeholder, height = 44, bg = '#fff', ariaLabel }) {
  return (
    <div className="flex items-center rounded-full" style={{ gap: 10, height, padding: '0 16px', background: bg }}>
      <SearchIcon />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={ariaLabel || placeholder} className="min-w-0 flex-1 border-0 bg-transparent font-mono text-[12px] outline-none focus:ring-0" style={{ padding: 0 }} />
    </div>
  );
}

/**
 * A tinted panel with a pill search and toggle pills: picked rows are black with a cyan
 * initials circle; results are white. `search(q) → [{ id, label, sub }]`.
 */
function PickPanel({ title, placeholder, search, picked, labels, onToggle, minChars = 0 }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const timer = useRef(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < minChars) { setRows([]); return undefined; }
    timer.current = setTimeout(async () => setRows(await search(term)), 250);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const items = [
    ...picked.map((id) => ({ id, label: labels[id] || `${String(id).slice(0, 8)}…`, on: true })),
    ...rows.filter((r) => !picked.includes(r.id)).map((r) => ({ ...r, on: false })),
  ];
  return (
    <div className="flex flex-col" style={{ gap: 10, padding: 16, borderRadius: 22, background: '#F5F5F5' }}>
      <Label>{title}</Label>
      <PillSearch value={q} onChange={setQ} placeholder={placeholder} />
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {items.map((p) => (
          <button key={p.id} type="button" aria-pressed={p.on} onClick={() => onToggle(p)} className={PILL} style={{ height: 40, padding: '0 14px 0 4px', gap: 8, fontSize: 11, fontWeight: 700, textTransform: 'none', letterSpacing: 0, background: p.on ? '#0A0A0A' : '#fff', color: p.on ? '#fff' : '#000' }}>
            <span className="flex items-center justify-center rounded-full font-mono text-[9px] font-extrabold text-black" style={{ width: 32, height: 32, background: p.on ? '#22ECFB' : '#F2F2F2' }}>{initials(p.label)}</span>
            {p.label}{p.sub ? <span style={{ opacity: .6 }}> · {p.sub}</span> : null}
          </button>
        ))}
        {items.length === 0 && <span className="font-mono text-[10px] font-extrabold tracking-[0.14em] text-[#9CA3AF]" style={{ padding: '12px 4px' }}>{q.trim().length < minChars ? `TYPE ${minChars} LETTERS TO SEARCH` : 'NO MATCHES'}</span>}
      </div>
    </div>
  );
}

export default function AudienceEditor({ value, onChange, legacyCity = null, onReach, narrow = false }) {
  const audience = { ...BLANK_AUDIENCE, ...(value?.audience || {}) };
  const cities = value?.cities || [];
  const labels = audience.labels || {};
  const [catalog, setCatalog] = useState([]);
  const [cityQ, setCityQ] = useState('');
  const [geo, setGeo] = useState([]);
  const [reach, setReach] = useState(null);
  const reachTimer = useRef(null);
  const geoTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => { const r = await portalService.listCities(); if (!cancelled && r.success) setCatalog(r.data || []); })();
    return () => { cancelled = true; };
  }, []);

  // Any city: the catalog answers at once, Mapbox fills in the rest.
  useEffect(() => {
    if (geoTimer.current) clearTimeout(geoTimer.current);
    const term = cityQ.trim();
    if (term.length < 3) { setGeo([]); return undefined; }
    geoTimer.current = setTimeout(async () => { try { setGeo(await geocodeCities(term)); } catch { setGeo([]); } }, 300);
    return () => geoTimer.current && clearTimeout(geoTimer.current);
  }, [cityQ]);

  // REACH — debounced, one request per settled edit.
  const key = JSON.stringify({ s: audience.scopes, c: audience.clubIds, u: audience.userIds, n: audience.newWithinDays, x: cities.map((c) => [c.lat, c.lng, c.radiusMiles]) });
  useEffect(() => {
    if (reachTimer.current) clearTimeout(reachTimer.current);
    setReach(null);
    onReach?.(null);
    reachTimer.current = setTimeout(async () => {
      const r = await appNoticeService.audienceCount({ scopes: audience.scopes, clubIds: audience.clubIds, userIds: audience.userIds, newWithinDays: audience.newWithinDays }, cities);
      const n = r.success ? (r.data?.count ?? 0) : null;
      setReach(n);
      onReach?.(n);
    }, 400);
    return () => reachTimer.current && clearTimeout(reachTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setAudience = (patch) => onChange({ audience: { ...audience, ...patch }, cities });
  const setCities = (next) => onChange({ audience, cities: next });

  const toggleScope = (k) => {
    if (k === 'everyone') { setAudience({ scopes: [], clubIds: [], userIds: [], newWithinDays: null }); return; }
    const on = audience.scopes.includes(k);
    const scopes = on ? audience.scopes.filter((s) => s !== k) : [...audience.scopes.filter((s) => s !== 'everyone'), k];
    const patch = { scopes };
    if (on && k === 'clubs') patch.clubIds = [];
    if (on && k === 'select') patch.userIds = [];
    if (k === 'new') patch.newWithinDays = on ? null : (audience.newWithinDays || 30);
    setAudience(patch);
  };
  const everyone = audience.scopes.length === 0;
  const withLabel = (id, label) => ({ ...labels, [id]: label });

  const addCity = (c) => {
    if (!c || cities.some((x) => x.slug === c.slug)) return;
    setCities([...cities, { slug: c.slug, label: c.label, lat: c.lat, lng: c.lng, radiusMiles: 50 }]);
    setCityQ('');
  };
  const cq = cityQ.trim().toLowerCase();
  const cityOptions = useMemo(() => {
    const fromCatalog = catalog.filter((c) => !cities.some((x) => x.slug === c.slug) && (!cq || c.label.toLowerCase().includes(cq) || (c.region || '').toLowerCase().includes(cq)));
    const extra = geo.filter((g) => !cities.some((x) => x.slug === g.slug) && !fromCatalog.some((c) => c.slug === g.slug));
    return [...fromCatalog, ...extra].slice(0, cq ? 16 : 10);
  }, [catalog, geo, cities, cq]);

  const searchUsers = async (term) => {
    const r = await appNoticeService.people(term);
    return r.success ? (r.data || []).map((u) => ({ id: u.id, label: u.displayName || u.email, sub: u.displayName ? (u.handle ? `@${u.handle}` : u.email) : null })) : [];
  };
  const searchClubs = async (term) => {
    const r = await appNoticeService.clubs(term);
    return r.success ? (r.data || []).map((c) => ({ id: c.id, label: c.name })) : [];
  };

  const reachText = reach == null ? 'COUNTING…' : peopleNoun(reach);
  return (
    <StudioCard title="WHO SEES IT" narrow={narrow} testId="audience-editor"
      right={<span className="font-mono text-[10px] tracking-[0.12em] text-[#6B7280]">REACH <b className="text-black" data-testid="reach">{reachText}</b></span>}>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
        {SCOPES.map((s) => {
          const on = s.key === 'everyone' ? everyone : audience.scopes.includes(s.key);
          return (
            <button key={s.key} type="button" aria-pressed={on} onClick={() => toggleScope(s.key)} className="flex flex-col items-start text-left transition-colors duration-200" style={{ gap: 6, padding: '16px 18px', border: 'none', borderRadius: 22, ...pillOf(on), fontFamily: 'inherit', cursor: 'pointer' }}>
              <span className="font-mono text-[11px] font-extrabold tracking-[0.14em]">{s.label}</span>
              <span className="font-mono text-[10px] leading-[1.5]" style={{ opacity: .7 }}>{s.hint}</span>
            </button>
          );
        })}
      </div>
      {audience.scopes.length > 1 && <span className="font-mono text-[10px] text-[#6B7280]">Groups add together. Anyone in any of them sees it.</span>}

      {audience.scopes.includes('clubs') && (
        <PickPanel title={`WHICH CLUBS · ${audience.clubIds.length} PICKED`} placeholder="Search clubs…" search={searchClubs} picked={audience.clubIds} labels={labels}
          onToggle={(p) => setAudience(p.on ? { clubIds: audience.clubIds.filter((x) => x !== p.id) } : { clubIds: [...audience.clubIds, p.id], labels: withLabel(p.id, p.label) })} />
      )}
      {audience.scopes.includes('select') && (
        <PickPanel title={`WHICH PEOPLE · ${audience.userIds.length} PICKED`} placeholder="Search by name, @handle or email…" search={searchUsers} picked={audience.userIds} labels={labels} minChars={2}
          onToggle={(p) => setAudience(p.on ? { userIds: audience.userIds.filter((x) => x !== p.id) } : { userIds: [...audience.userIds, p.id], labels: withLabel(p.id, p.label) })} />
      )}
      {audience.scopes.includes('new') && (
        <div className="flex flex-wrap items-center" style={{ gap: 10, padding: '12px 16px', borderRadius: 22, background: '#F5F5F5' }}>
          <Label>SIGNED UP WITHIN</Label>
          <div className="flex" style={{ gap: 4, padding: 4, borderRadius: 999, background: '#fff' }} role="radiogroup" aria-label="Signed up within">
            {[7, 14, 30, 90].map((d) => (
              <button key={d} type="button" role="radio" aria-checked={audience.newWithinDays === d} onClick={() => setAudience({ newWithinDays: d })} className={PILL} style={{ height: 32, padding: '0 14px', fontSize: 10, letterSpacing: '.12em', ...pillOf(audience.newWithinDays === d) }}>{d} DAYS</button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col" style={{ gap: 10, paddingTop: 16, borderTop: '1px solid #EDEDED' }}>
        <div className="flex flex-wrap items-baseline justify-between" style={{ gap: 12 }}>
          <Label>{cities.length ? 'CITIES · ONLY PEOPLE INSIDE THESE' : 'CITIES · ANYWHERE'}</Label>
          {cities.length > 0 && <button type="button" onClick={() => setCities([])} className="border-0 bg-transparent p-0 font-mono text-[10px] font-extrabold tracking-[0.12em] text-[#6B7280] hover:text-black">CLEAR CITIES</button>}
        </div>
        {legacyCity && cities.length === 0 && (
          <p className="m-0 font-mono text-[10px] text-[#B45309]">This notice still uses the old text match "{legacyCity}". Pick the city below to turn it into a real geofence.</p>
        )}
        {cities.length > 0 && (
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            {cities.map((c) => (
              <span key={c.slug} className="flex items-center rounded-full font-mono text-[10px] font-extrabold tracking-[0.12em] text-white" style={{ height: 40, padding: '0 4px 0 16px', gap: 8, background: '#0A0A0A' }}>
                {cityName(c.label).toUpperCase()}
                <select aria-label={`Radius around ${cityName(c.label)}`} value={c.radiusMiles} onChange={(e) => setCities(cities.map((x) => (x.slug === c.slug ? { ...x, radiusMiles: Number(e.target.value) } : x)))}
                  className="cursor-pointer appearance-none rounded-full border-0 font-mono text-[10px] font-extrabold tracking-[0.1em] text-white focus:ring-0" style={{ height: 30, padding: '0 10px', background: '#2E2E2E' }}>
                  {RADIUS_OPTIONS.map((m) => <option key={m} value={m}>{m} MI</option>)}
                </select>
                <button type="button" aria-label={`Remove ${cityName(c.label)}`} onClick={() => setCities(cities.filter((x) => x.slug !== c.slug))} className="flex items-center justify-center rounded-full bg-transparent text-white hover:bg-[#2E2E2E]" style={{ width: 30, height: 30 }}>
                  <Icon d={ICON.close} size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        <PillSearch value={cityQ} onChange={setCityQ} placeholder="Search any city…" height={46} bg="#F5F5F5" />
        <div className="flex flex-wrap" style={{ gap: 6 }}>
          {cityOptions.map((c) => (
            <button key={c.slug} type="button" onClick={() => addCity(c)} className={`${PILL} hover:bg-[#0A0A0A] hover:text-white`} style={{ height: 36, padding: '0 14px', fontSize: 10, letterSpacing: '.12em', background: '#F2F2F2', color: '#000' }}>+ {cityName(c.label).toUpperCase()}</button>
          ))}
        </div>
      </div>

      <div className="font-mono text-[11px] leading-[1.6] text-[#4B5563]" style={{ padding: '14px 18px', borderRadius: 18, background: '#F5F5F5' }} data-testid="reach-line">
        {describeTarget(audience, cities)}. Counted right now. Test and system accounts are never counted.
      </div>
    </StudioCard>
  );
}
